import type { FiltersState, ColumnConfig, ColumnDataType } from '../core/types';

/**
 * Applies filters to data using the bazza/ui filter state.
 * This is a client-side filtering utility for use with useDataTableFilters hook.
 */
export function applyFilters<TData>(
  data: TData[],
  filters: FiltersState,
  columnsConfig: ReadonlyArray<ColumnConfig<TData, any, any, any>>
): TData[] {
  if (filters.length === 0) return data;

  return data.filter((row) => {
    return filters.every((filter) => {
      const column = columnsConfig.find((c) => c.id === filter.columnId);
      if (!column) return true;

      const value = column.accessor(row);
      const filterValues = filter.values;

      if (filterValues.length === 0) return true;

      switch (filter.type) {
        case 'text': {
          const textValue = String(value || '').toLowerCase();
          const filterText = String(filterValues[0] || '').toLowerCase();
          if (filter.operator === 'contains') {
            return textValue.includes(filterText);
          } else if (filter.operator === 'does not contain') {
            return !textValue.includes(filterText);
          }
          return true;
        }
        case 'option': {
          const strValue = String(value);
          if (filter.operator === 'is') {
            return strValue === filterValues[0];
          } else if (filter.operator === 'is not') {
            return strValue !== filterValues[0];
          } else if (filter.operator === 'is any of') {
            return filterValues.includes(strValue);
          } else if (filter.operator === 'is none of') {
            return !filterValues.includes(strValue);
          }
          return true;
        }
        case 'multiOption': {
          const arrValue = Array.isArray(value) ? value : [value];
          if (filter.operator === 'include') {
            return arrValue.includes(filterValues[0]);
          } else if (filter.operator === 'exclude') {
            return !arrValue.includes(filterValues[0]);
          } else if (filter.operator === 'include any of') {
            return filterValues.some((fv) => arrValue.includes(fv));
          } else if (filter.operator === 'include all of') {
            return filterValues.every((fv) => arrValue.includes(fv));
          } else if (filter.operator === 'exclude if any of') {
            return !filterValues.some((fv) => arrValue.includes(fv));
          } else if (filter.operator === 'exclude if all') {
            return !filterValues.every((fv) => arrValue.includes(fv));
          }
          return true;
        }
        case 'number': {
          const numValue = Number(value);
          if (isNaN(numValue)) return true;
          if (filter.operator === 'is') {
            return numValue === filterValues[0];
          } else if (filter.operator === 'is not') {
            return numValue !== filterValues[0];
          } else if (filter.operator === 'is greater than') {
            return numValue > filterValues[0];
          } else if (filter.operator === 'is greater than or equal to') {
            return numValue >= filterValues[0];
          } else if (filter.operator === 'is less than') {
            return numValue < filterValues[0];
          } else if (filter.operator === 'is less than or equal to') {
            return numValue <= filterValues[0];
          } else if (filter.operator === 'is between') {
            return numValue >= filterValues[0] && numValue <= filterValues[1];
          } else if (filter.operator === 'is not between') {
            return numValue < filterValues[0] || numValue > filterValues[1];
          }
          return true;
        }
        case 'date': {
          const dateValue = value instanceof Date ? value : new Date(value as string);
          if (isNaN(dateValue.getTime())) return true;
          const filterDate = filterValues[0] instanceof Date ? filterValues[0] : new Date(filterValues[0] as string);

          if (filter.operator === 'is') {
            return dateValue.toDateString() === filterDate.toDateString();
          } else if (filter.operator === 'is not') {
            return dateValue.toDateString() !== filterDate.toDateString();
          } else if (filter.operator === 'is before') {
            return dateValue < filterDate;
          } else if (filter.operator === 'is after') {
            return dateValue > filterDate;
          } else if (filter.operator === 'is on or before') {
            return dateValue <= filterDate;
          } else if (filter.operator === 'is on or after') {
            return dateValue >= filterDate;
          } else if (filter.operator === 'is between') {
            const filterDate2 = filterValues[1] instanceof Date ? filterValues[1] : new Date(filterValues[1] as string);
            return dateValue >= filterDate && dateValue <= filterDate2;
          } else if (filter.operator === 'is not between') {
            const filterDate2 = filterValues[1] instanceof Date ? filterValues[1] : new Date(filterValues[1] as string);
            return dateValue < filterDate || dateValue > filterDate2;
          }
          return true;
        }
        default:
          return true;
      }
    });
  });
}
